import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import MessageWithLink from './MessageWithLink';
import { keyframes } from '@emotion/react';
import Chatbot, { ChatBotHandle } from './ChatBot';
import LoginPopup from './components/LoginPopup';
import { EnvironmentProvider, useEnvironment } from './contexts/EnvironmentContext';
import default_messages from './data/messages';
import { WidgetController, NETAI_WIDGET_PREFILL_EVENT, PENDING_MESSAGE_KEY, PENDING_AUTO_SEND_KEY } from './widgetTypes';

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 8px rgba(139, 92, 246, 0.4),
                0 0 12px rgba(139, 92, 246, 0.3),
                0 0 16px rgba(6, 182, 212, 0.2);
  }
  50% {
    box-shadow: 0 0 14px rgba(139, 92, 246, 0.6),
                0 0 20px rgba(139, 92, 246, 0.4),
                0 0 24px rgba(6, 182, 212, 0.35);
  }
  100% {
    box-shadow: 0 0 8px rgba(139, 92, 246, 0.4),
                0 0 12px rgba(139, 92, 246, 0.3),
                0 0 16px rgba(6, 182, 212, 0.2);
  }
`;

const animateButton = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1); // The button grows 10% larger
  }
  100% {
    transform: scale(1);
  }
`;

const slideUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface IChatFab {
  scope: string;
  env: string;
  search_level: string;
  onRegister?: (controller: WidgetController | null) => void;
}

// Dynamic container with positioning based on backtotop_container presence
const ChatFabContainer = styled.div<{ $isBackToTopVisible: boolean }>`
  position: fixed;
  bottom: 25px;
  right: ${props => props.$isBackToTopVisible ? '70px' : '25px'};
  transition: all 0.3s ease;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
`;

const ChatFabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background-color: transparent;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  transition: transform 0.3s;
  animation: ${animateButton} 2s infinite ease-in-out;
  box-shadow: 0 4px 14px -4px rgba(139, 92, 246, 0.5);
`;

const ChatFabImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 14px;
  object-fit: cover;
  display: block;
  animation: ${pulseGlow} 2s infinite ease-in-out;
`;

const ChatbotContainer = styled.div`
  position: absolute;
  bottom: 80px;
  right: 0;
  z-index: 9998;
  width: min(750px, calc(100vw - 40px));
  max-width: 750px;
  height: min(720px, calc(100vh - 120px));
  max-height: calc(100vh - 120px);
  animation: ${slideUp} 0.3s ease-out;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 24px 64px -20px rgba(15, 23, 42, 0.28),
    0 8px 24px -12px rgba(15, 23, 42, 0.16);

  @media (max-width: 760px) {
    right: 20px;
    width: calc(100vw - 60px);
    height: min(720px, calc(100vh - 100px));
    bottom: 80px;
  }

  @media (max-width: 480px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    z-index: 10000;
  }
`;

// Internal component that uses the environment context
const ChatFabInternal: React.FC<{ scope: string, env:string, search_level:string, onRegister?: (controller: WidgetController | null) => void }> = ({ scope, env, search_level, onRegister }) => {
  const { config, getLoginUrl, getMaiaIconUrl } = useEnvironment();
  const [isHovered, setIsHovered] = useState(false);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isBackToTopVisible, setIsBackToTopVisible] = useState(false);
  const [prefillMessage, setPrefillMessage] = useState<string | null>(null);
  const [autoSendMessage, setAutoSendMessage] = useState(false);
  const chatBotRef = useRef<ChatBotHandle>(null);

  const message = default_messages.find((m) => m.scope === config.scope);

  // Check for existing user details on component mount
  useEffect(() => {
    const checkAuthentication = () => {
      const userDetails = localStorage.getItem('userDetails');
      if (userDetails) {
        try {
          const user = JSON.parse(userDetails);
          // Validate user object has required properties
          if (user && (user.email || user.username) && (user.session_id || user.sso_id)) {
            console.log('User already authenticated:', user);
            setIsAuthenticated(true);
            return true;
          }
        } catch (error) {
          console.error('Error parsing user details:', error);
          localStorage.removeItem('userDetails');
        }
      }
      return false;
    };

    // Check authentication status on mount
    checkAuthentication();

    // Function to check back-to-top container visibility
    const checkBackToTopVisibility = () => {
      const backToTopContainer = document.getElementById('backtotop_container') || 
                                 document.getElementById('backtotop_contianer'); // Handle potential typo
      
      if (backToTopContainer) {
        const isVisible = window.getComputedStyle(backToTopContainer).display !== 'none' &&
                         backToTopContainer.style.display !== 'none';
        setIsBackToTopVisible(isVisible);
      } else {
        setIsBackToTopVisible(false);
      }
    };

    // Check initially
    checkBackToTopVisibility();

    // Add scroll listener to check back-to-top visibility
    const handleScroll = () => {
      checkBackToTopVisibility();
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkBackToTopVisibility); // Also check on resize

    // Add MutationObserver for better back-to-top container detection
    const backToTopContainer = document.getElementById('backtotop_container') || 
                               document.getElementById('backtotop_contianer'); // Handle potential typo

    let observer: MutationObserver | null = null;
    if (backToTopContainer) {
      observer = new MutationObserver(checkBackToTopVisibility);
      observer.observe(backToTopContainer, { attributes: true, childList: true, subtree: true });
    }

    // Listen for authentication success message from the login window
    const handleAuthMessage = (event: MessageEvent) => {
      console.log('Message received from popup:', event.data);
      console.log('Event origin:', event.origin);
      
      // Define allowed origins for security
      const allowedOrigins = [
        'https://maia-dev.netapp.com',
        'https://maia-stg.netapp.com',
        'https://maia.netapp.com',
        'https://netaiwidgets-dev.netapp.com',
        'https://netaiwidgets-stg.netapp.com', 
        'https://netaiwidgets.netapp.com'
      ];
      
      // Handle authentication response
      if (event.data && event.data.type === 'auth-response') {
        if (event.data.status === 'success') {
          const userDetails = {
            username: event.data.username || '',
            session_id: event.data.session_id || '',
            email: event.data.email || '',
            sso_id: event.data.sso_id || '',
            timestamp: new Date().toISOString()
          };
          
          console.log('Authentication successful! Storing user details:', userDetails);
          
          // Save user details to localStorage
          localStorage.setItem('userDetails', JSON.stringify(userDetails));
          setIsAuthenticated(true);
          setShowLoginPopup(false);
          
          // Open chat after successful authentication
          setTimeout(() => {
            const pending = sessionStorage.getItem(PENDING_MESSAGE_KEY);
            const pendingAutoSend = sessionStorage.getItem(PENDING_AUTO_SEND_KEY) === 'true';
            if (pending) {
              setPrefillMessage(pending);
            }
            setAutoSendMessage(pendingAutoSend);
            setIsChatbotOpen(true);
          }, 100);
          
        } else if (event.data.status === 'error') {
          console.error('Authentication failed:', event.data.message || 'Unknown error');
          setShowLoginPopup(false);
          // Optionally show error message to user
        }
      }
    };

    window.addEventListener('message', handleAuthMessage);
    
    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('message', handleAuthMessage);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkBackToTopVisibility);
    };
  }, []);

  const handleLogin = useCallback(() => {
    setShowLoginPopup(false);

    const loginWindow = window.open(
      getLoginUrl(),
      'login',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    if (!loginWindow) {
      console.error('Failed to open login window. Please check popup blocker settings.');
      setShowLoginPopup(true);
      return;
    }

    const checkClosed = setInterval(() => {
      if (loginWindow?.closed) {
        clearInterval(checkClosed);
        console.log('Login window was closed');
      }
    }, 1000);

    setTimeout(() => {
      if (loginWindow && !loginWindow.closed) {
        console.log('Login process taking longer than expected');
      }
    }, 30000);
  }, [getLoginUrl]);

  const handleLoginCancel = () => {
    setShowLoginPopup(false);
  };

  const openWithMessage = useCallback((message: string, autoSend = false) => {
    setPrefillMessage(message);
    setAutoSendMessage(autoSend);
    sessionStorage.setItem(PENDING_MESSAGE_KEY, message);
    if (autoSend) {
      sessionStorage.setItem(PENDING_AUTO_SEND_KEY, 'true');
    } else {
      sessionStorage.removeItem(PENDING_AUTO_SEND_KEY);
    }

    const userDetails = localStorage.getItem('userDetails');
    if (userDetails) {
      try {
        const user = JSON.parse(userDetails);
        if (user && (user.email || user.username) && (user.session_id || user.sso_id)) {
          setIsAuthenticated(true);
          setIsChatbotOpen(true);
          setIsHovered(false);
          return;
        }
      } catch (error) {
        console.error('Error parsing stored user details:', error);
        localStorage.removeItem('userDetails');
      }
    }

    setIsAuthenticated(false);
    handleLogin();
    setIsHovered(false);
  }, [handleLogin]);

  const openWithMessageRef = useRef(openWithMessage);
  openWithMessageRef.current = openWithMessage;

  useLayoutEffect(() => {
    if (!onRegister) return;

    const controller: WidgetController = {
      openWithMessage: (message: string, autoSend?: boolean) => openWithMessageRef.current(message, autoSend),
    };

    onRegister(controller);

    if (window.NetAIWidget) {
      window.NetAIWidget.openWithMessage = controller.openWithMessage;
    }

    return () => {
      onRegister(null);
    };
  }, [onRegister]);

  useEffect(() => {
    const handlePrefillEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ message?: string; autoSend?: boolean }>;
      const message = customEvent.detail?.message;
      if (message) {
        openWithMessageRef.current(message, customEvent.detail?.autoSend);
      }
    };

    window.addEventListener(NETAI_WIDGET_PREFILL_EVENT, handlePrefillEvent);
    return () => window.removeEventListener(NETAI_WIDGET_PREFILL_EVENT, handlePrefillEvent);
  }, []);

  useEffect(() => {
    if (isChatbotOpen && isAuthenticated && prefillMessage && !autoSendMessage) {
      const applyPrefill = () => chatBotRef.current?.setInput(prefillMessage);
      applyPrefill();
      const timer = setTimeout(applyPrefill, 100);
      return () => clearTimeout(timer);
    }
  }, [prefillMessage, autoSendMessage, isChatbotOpen, isAuthenticated]);

  const handleClick = () => {
    if (isChatbotOpen) {
      setIsChatbotOpen(false);
      setIsHovered(false);
      return;
    }

    const userDetails = localStorage.getItem('userDetails');
    if (userDetails) {
      try {
        const user = JSON.parse(userDetails);
        if (user && (user.email || user.username) && (user.session_id || user.sso_id)) {
          setIsAuthenticated(true);
          setIsChatbotOpen(true);
          setIsHovered(false);
          return;
        }
      } catch (error) {
        console.error('Error parsing stored user details:', error);
        localStorage.removeItem('userDetails');
      }
    }

    setIsAuthenticated(false);
    handleLogin();
    setIsHovered(false);
  };

  const handleChatClose = () => {
    setIsChatbotOpen(false);
  };

  if (!message) {
    return null;
  }

  return (
    <ChatFabContainer $isBackToTopVisible={isBackToTopVisible}>
      <MessageWithLink
        message={message.message}
        isVisible={isHovered && !isChatbotOpen && !showLoginPopup} 
      />
      <ChatFabButton
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        id="chat-fab"
        onClick={handleClick}
        className={isChatbotOpen ? 'open' : ''}
      >
        <ChatFabImage src={getMaiaIconUrl()} alt="maia Chat" />
      </ChatFabButton>
      
      {showLoginPopup && (
        <LoginPopup
          onLogin={handleLogin}
          onCancel={handleLoginCancel}
        />
      )}
      
      {isChatbotOpen && isAuthenticated && (
        <ChatbotContainer>
          <Chatbot
            ref={chatBotRef}
            onClose={handleChatClose}
            scope={scope}
            env={env}
            search_level={search_level}
            initialMessage={prefillMessage}
            autoSend={autoSendMessage}
          />
        </ChatbotContainer>
      )}
    </ChatFabContainer>
  );
};

// Main ChatFab component that provides the environment context
const ChatFab: React.FC<IChatFab> = ({ scope, env, search_level, onRegister }) => {
  return (
    <EnvironmentProvider env={env} scope={scope}>
      <ChatFabInternal scope={scope} env={env} search_level={search_level} onRegister={onRegister} />
    </EnvironmentProvider>
  );
};

export default ChatFab;
